<template>
  <Layout>
    <template #header-container>
      <div
        :class="['mc-list-header', { 'mc-list-header--sticky': isStickyHeader }]"
        :style="mobileHeaderCssVars"
      >
        <PageHeader :title="title" :isSticky="!isMobileHeader || isStickyHeader">
          <template v-if="isMobileHeader" #beforeTitle>
            <CancelConfirmation
              class="mc-create-bulk__cancel"
              :screenBreakpoint="buttonScreenBreakpoint"
              :displayTooltip="true"
              tooltipPosition="bottom"
            />
          </template>
          <template v-if="!isMobileHeader" #afterTitle>
            <div class="d-flex align-items-center gap-2 mc-create-bulk__header-actions">
              <CancelConfirmation />
              <McButton
                :variant="hasFeatureScheduleFoFromBulkDoMGM ? 'secondary' : 'primary'"
                icon="icon-outline_save"
                :screenBreakpoint="
                  hasFeatureScheduleFoFromBulkDoMGM ? buttonScreenBreakpoint : null
                "
                :displayTooltip="hasFeatureScheduleFoFromBulkDoMGM"
                tooltipPosition="bottom"
                :text="
                  hasFeatureScheduleFoFromBulkDoMGM
                    ? $t('global.action.saveAsDraft')
                    : $t('global.save')
                "
                @click="handleSubmit()"
              />
              <McButton
                v-if="hasFeatureScheduleFoFromBulkDoMGM"
                variant="primary"
                class="mc-create-bulk__primary-action"
                icon="icon-outline_fleet-order"
                :text="$t('global.action.saveAndSchedule')"
                @click="handleScheduleFoFromBulkDo"
              />
            </div>
          </template>
          <template v-if="isMobileHeader" #footer>
            <div
              class="d-flex align-items-center gap-2 mc-create-bulk__header-actions mc-create-bulk__header-actions--mobile"
            >
              <McButton
                :variant="hasFeatureScheduleFoFromBulkDoMGM ? 'secondary' : 'primary'"
                :class="{
                  'mc-create-bulk__primary-action': !hasFeatureScheduleFoFromBulkDoMGM,
                }"
                icon="icon-outline_save"
                :screenBreakpoint="
                  hasFeatureScheduleFoFromBulkDoMGM ? buttonScreenBreakpoint : null
                "
                :displayTooltip="hasFeatureScheduleFoFromBulkDoMGM"
                tooltipPosition="bottom"
                :text="
                  hasFeatureScheduleFoFromBulkDoMGM
                    ? $t('global.action.saveAsDraft')
                    : $t('global.save')
                "
                @click="handleSubmit()"
              />
              <McButton
                v-if="hasFeatureScheduleFoFromBulkDoMGM"
                variant="primary"
                class="mc-create-bulk__primary-action"
                icon="icon-outline_fleet-order"
                :text="$t('global.action.saveAndSchedule')"
                @click="handleScheduleFoFromBulkDo"
              />
            </div>
          </template>
        </PageHeader>
      </div>
    </template>

    <FormulateForm ref="create-bulk-form" :errors="errors">
      <FormSection
        :title="$t('delivery.basicInformation')"
        :isError="isError.basicInfo"
        :formValues="bulkFormValues"
        :fullObjects="bulkFullObjects"
        :fields="basicInfoFields"
        @update:formValues="handleUpdateFormValues($event)"
        @update:fullObjects="handleUpdateFullObjects($event)"
      >
        <template #billed_customer_id="{ field }">
          <FormulateInput
            class="p-0"
            v-bind="field"
            validation-behavior="live"
            :value="bulkFormValues?.customer_id"
            :fullObject="bulkFullObjects?.customer_id"
          />
        </template>
        <template #marketing_user_id="{ field }">
          <UserMktSelect
            :field="field"
            :customerFullObject="bulkFullObjects?.customer_id"
            :defaultFullObject="loggedInMarketingUser"
            :value="bulkFormValues?.marketing_user_id"
            :fullObject="bulkFullObjects?.marketing_user_id"
            @input="($event) => handleUpdateFormValues({ key: field.name, value: $event })"
            @update:fullObject="
              ($event) => handleUpdateFullObjects({ key: field.name, value: $event })
            "
          />
        </template>
      </FormSection>
      <FormSection
        v-if="isCustomFieldShown"
        :title="$t('delivery.additionalFields.title')"
        :isError="isError.customField"
        :formValues="bulkFormValues"
        :fullObjects="bulkFullObjects"
        :fields="computedCustomFields"
        @update:formValues="handleUpdateFormValues($event)"
        @update:fullObjects="handleUpdateFullObjects($event)"
      />

      <ListDelivery :isError="isError.deliveries" />
    </FormulateForm>

    <div
      v-if="isMobileHeader"
      aria-hidden="true"
      class="mc-create-bulk__mobile-bottom-spacer"
      :style="{ height: `${mobileBottomSpacerHeight}px` }"
    />

    <TemperatureSummary
      :data="dataGapTemprerature"
      :modal-id="'modal-confirm-summary-temperature-attach'"
      @handleSubmit="handleApplyCreateFO"
    />

    <ScheduleFleetTask :parseData="parseData()" @handleSubmit="handleSubmitCreateFO" />
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import { mapState } from 'vuex'
import { mapGetters } from 'vuex'
import { EventBus } from './lib/event-bus'

import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2'
import McButton from '@/components/McButton'
import CancelConfirmation from './components/CancelConfirmation'
import FormSection from './components/FormSection'
import ListDelivery from './components/ListDelivery'
import UserMktSelect from './components/wrapper/UserMktSelect'
import TemperatureSummary from './components/modal/TemperatureSummary.vue'
import ScheduleFleetTask from './components/modal/ScheduleFleetTask.vue'

import * as CustomField from '@/services/rest-api/lib/setting/custom-field'
import { getBasicInfoFields } from './lib/fields/basic-info-fields.js'
import { getOriginFields, getDestinationFields } from './lib/fields/delivery-detail-fields.js'
import { getLoadFields } from './lib/fields/load-fields.js'
import {
  getApiErrorMappings,
  getApiErrorMappingMessages,
} from './lib/constants/api-error-mappings.js'

import cloneDeep from 'lodash/cloneDeep'
import omitBy from 'lodash/omitBy'
import { Helper } from '@/mixin/helper'
import * as DeliveryOrder from '@/services/rest-api/lib/delivery/delivery-order'

export default {
  page: {
    title: 'Delivery',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    CancelConfirmation,
    FormSection,
    ListDelivery,
    UserMktSelect,
    TemperatureSummary,
    ScheduleFleetTask,
  },
  mixins: [Helper],
  data() {
    return {
      customFields: [],

      isError: {
        basicInfo: false,
        customField: false,
        deliveries: false,
      },

      errors: {},
      validationErrorKeys: [],
      duplicateShipmentErrorKeys: [],
      loadValidationErrorKeys: [],
      formValues: {},
      fullObjects: {},

      dataGapTemprerature: null,
      skipCheckTemp: false,
      dontShowAgain: false,
      isMobileHeader: false,
      hasPageScrolled: false,
      mobileTopbarHeight: 70,
      mobileHeaderHeight: 0,
      mobileMenuHeight: 70,
    }
  },
  computed: {
    ...mapState('delivery', [
      'bulkDeliveryIds',
      'bulkFormValues',
      'bulkItemValues',
      'bulkLoadValues',

      'bulkFullObjects',
    ]),
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagData: 'identity/getFeatureFlagData',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    basicInfoFields() {
      return getBasicInfoFields((key, params) => this.$t(key, params))
    },
    originFields() {
      return getOriginFields((key, params) => this.$t(key, params))
    },
    destinationFields() {
      return getDestinationFields((key, params) => this.$t(key, params))
    },
    loadFields() {
      return getLoadFields((key, params) => this.$t(key, params))
    },
    apiErrorMappings() {
      return getApiErrorMappings((key, params) => this.$t(key, params))
    },
    apiErrorMappingMessages() {
      return getApiErrorMappingMessages((key, params) => this.$t(key, params))
    },
    title() {
      return this.$t('delivery.action.createBulk')
    },
    buttonScreenBreakpoint() {
      return 768
    },
    computedCustomFields() {
      return this.transformCustomFieldResponse(this.customFields)
    },
    computedBasicInfoFieldKeys() {
      return this.basicInfoFields.map((field) => field.name)
    },
    computedCustomFieldKeys() {
      return this.computedCustomFields.map((field) => field.name)
    },
    computedDeliveryFieldKeys() {
      return [
        ...this.originFields.map((field) => field.name),
        ...this.destinationFields.map((field) => field.name),
        ...this.loadFields.map((field) => field.name),
      ]
    },
    isCustomFieldShown() {
      return this.computedCustomFields.length > 0
    },
    hasFeatureScheduleFoFromBulkDoMGM() {
      const companyId = Helper.methods.getCompanyId()
      const data = this.getFeatureFlagValue('customFeature.mgm')
      return (
        Array.isArray(data?.company_id) &&
        data.company_id.includes(companyId) &&
        data?.features?.schedule_fo_from_bulk_do
      )
    },
    isStickyHeader() {
      return this.isMobileHeader && this.hasPageScrolled
    },
    mobileBottomSpacerHeight() {
      if (!this.isMobileHeader) return 0

      return this.mobileHeaderHeight + this.mobileMenuHeight + 24
    },
    mobileHeaderCssVars() {
      return {
        '--mc-create-bulk-sticky-top': `${this.mobileTopbarHeight}px`,
      }
    },
    loggedInMarketingUser() {
      const position = String(this.getUserPosition() || '')
        .trim()
        .toLowerCase()
      const isAllowedPosition = ['tms marketing', 'sales/marketing'].includes(position)
      const userId = this.getUserId()

      if (!isAllowedPosition || userId == null) return null

      return {
        id: Number(userId),
        userId: Number(userId),
        name: this.getUserName(),
        position: this.getUserPosition(),
      }
    },
    increaseDeliveryOrderLimit() {
      return this.getFeatureFlagValue('increaseDeliveryOrderLimitFlag')
    },
  },
  watch: {
    loggedInMarketingUser: {
      immediate: true,
      handler(newValue) {
        this.applyLoggedInMarketingUser(newValue)
      },
    },
    bulkItemValues: {
      deep: true,
      handler() {
        this.syncDuplicateShipmentErrors()
      },
    },
    bulkDeliveryIds() {
      this.syncDuplicateShipmentErrors()
    },
  },
  created() {
    EventBus.$on('remove-error', this.removeError)
  },
  async mounted() {
    this.checkHeaderViewport()
    this.handleWindowScroll()
    this.updateMobileLayoutMetrics()
    window.addEventListener('resize', this.checkHeaderViewport)
    window.addEventListener('scroll', this.handleWindowScroll, { passive: true })
    window.addEventListener('resize', this.updateMobileLayoutMetrics)
    await this.getCustomFields()
    this.$nextTick(() => {
      this.updateMobileLayoutMetrics()
    })
  },
  beforeDestroy() {
    this.$store.dispatch('delivery/resetBulkStates')
    EventBus.$off('remove-error', this.removeError)
    window.removeEventListener('resize', this.checkHeaderViewport)
    window.removeEventListener('scroll', this.handleWindowScroll)
    window.removeEventListener('resize', this.updateMobileLayoutMetrics)
  },
  methods: {
    handleWindowScroll() {
      this.hasPageScrolled = window.scrollY > 8
      this.updateMobileLayoutMetrics()
    },
    checkHeaderViewport() {
      this.isMobileHeader = window.innerWidth < this.buttonScreenBreakpoint
    },
    updateMobileLayoutMetrics() {
      if (!this.isMobileHeader) return

      this.$nextTick(() => {
        const topbar = document.getElementById('page-topbar')
        const mobileHeader = this.$el?.querySelector('.mc-list-header')
        const mobileMenu = document.querySelector('.mc-menu-mobile')

        this.mobileTopbarHeight = Math.ceil(topbar?.getBoundingClientRect().height || 70)
        this.mobileHeaderHeight = Math.ceil(mobileHeader?.getBoundingClientRect().height || 0)
        this.mobileMenuHeight = Math.ceil(mobileMenu?.getBoundingClientRect().height || 70)
      })
    },
    applyLoggedInMarketingUser(marketingUser = this.loggedInMarketingUser) {
      if (!marketingUser?.id) return

      const currentMarketingUserId = this.bulkFormValues?.marketing_user_id
      if (currentMarketingUserId != null && currentMarketingUserId !== '') return

      this.handleUpdateFormValues({ key: 'marketing_user_id', value: marketingUser.id })
      this.handleUpdateFullObjects({ key: 'marketing_user_id', value: marketingUser })
    },
    async getCustomFields() {
      const res = await CustomField.get()

      if (res.errors) return

      this.customFields = res.data
    },
    transformCustomFieldResponse(fields = []) {
      const fieldTypeMapping = { TEXT: 'text', NUMBER: 'number', DATE: 'date-picker' }
      const transformed = cloneDeep(fields)
        .map((field) => {
          const validation = []

          if (field.is_required) {
            validation.push('required:trim')
            field.help = true
          }

          if (field.type === 'NUMBER') {
            field._type = fieldTypeMapping.NUMBER
            field.type = fieldTypeMapping.TEXT
            validation.push('number')
          } else {
            field.type = fieldTypeMapping[field.type]
          }

          if (validation.length > 0) {
            field.validation = validation.join('|')
          }

          field.label = field.name
          field.name = field.key
          field.isShown = true

          return field
        })
        .filter((field) => field.is_active)

      return transformed
    },
    handleUpdateFormValues(payload) {
      this.$store.dispatch('delivery/setBulkFormValues', payload)
    },
    handleUpdateFullObjects(payload) {
      this.$store.dispatch('delivery/setBulkFullObjects', payload)
    },
    getShipmentNumberDuplicateMessage() {
      const key = 'delivery.validation.shipmentNumberDuplicate'
      const translated = this.$t(key)

      return translated === key
        ? 'No. Shipment tidak boleh sama dengan pengiriman lainnya.'
        : translated
    },
    getActiveErrorKeys() {
      return [...new Set([...Object.keys(this.errors), ...this.validationErrorKeys])]
    },
    syncErrorStates() {
      const activeErrorKeys = this.getActiveErrorKeys()
      const activeDeliveryIds = new Set(this.bulkDeliveryIds.map((id) => String(id)))
      const erroredDeliveryIds = new Set()
      const nextIsError = {
        basicInfo: false,
        customField: false,
        deliveries: false,
      }

      activeErrorKeys.forEach((key) => {
        const [fieldKey, deliveryId] = String(key).split('-')

        if (this.computedBasicInfoFieldKeys.includes(fieldKey)) {
          nextIsError.basicInfo = true
          return
        }

        if (this.computedCustomFieldKeys.includes(fieldKey)) {
          nextIsError.customField = true
          return
        }

        if (
          this.computedDeliveryFieldKeys.includes(fieldKey) &&
          deliveryId &&
          activeDeliveryIds.has(String(deliveryId))
        ) {
          nextIsError.deliveries = true
          erroredDeliveryIds.add(String(deliveryId))
        }
      })

      this.isError = nextIsError

      this.bulkDeliveryIds.forEach((id) => {
        this.$store.dispatch('delivery/setBulkIdError', {
          key: id,
          value: erroredDeliveryIds.has(String(id)),
        })
      })
    },
    syncDuplicateShipmentErrors() {
      this.duplicateShipmentErrorKeys.forEach((key) => {
        this.$delete(this.errors, key)
      })
      this.duplicateShipmentErrorKeys = []

      const shipmentNumbers = {}

      this.bulkDeliveryIds.forEach((id) => {
        const shipmentNumber = String(this.bulkItemValues?.[id]?.shipment_number || '').trim()

        if (!shipmentNumber) return

        if (!shipmentNumbers[shipmentNumber]) {
          shipmentNumbers[shipmentNumber] = []
        }

        shipmentNumbers[shipmentNumber].push(id)
      })

      Object.values(shipmentNumbers).forEach((ids) => {
        if (ids.length < 2) return

        ids.forEach((id) => {
          const errorKey = `shipment_number-${id}`

          this.$set(this.errors, errorKey, this.getShipmentNumberDuplicateMessage())
          this.duplicateShipmentErrorKeys.push(errorKey)
        })
      })

      this.syncErrorStates()
    },
    showErrors(ref) {
      this.validationErrorKeys = [
        ...new Set([...this.validationErrorKeys, ...Object.keys(ref.failingFields)]),
      ]
      this.syncErrorStates()
    },
    isFieldFilled(value) {
      if (Array.isArray(value)) return value.length > 0
      if (typeof value === 'string') return value.trim() !== ''

      return value !== null && value !== undefined && value !== false
    },
    clearLoadValidationErrors() {
      this.loadValidationErrorKeys.forEach((key) => {
        this.$delete(this.errors, key)
      })
      this.validationErrorKeys = this.validationErrorKeys.filter(
        (errorKey) => !this.loadValidationErrorKeys.includes(errorKey)
      )
      this.loadValidationErrorKeys = []
    },
    validateRequiredLoadFields() {
      this.clearLoadValidationErrors()

      const requiredLoadFields = this.loadFields.filter((field) =>
        field?.validation?.includes('required')
      )
      const nextErrorKeys = []

      this.bulkDeliveryIds.forEach((id) => {
        const loads = this.bulkLoadValues?.[id] || []

        loads.forEach((load, index) => {
          requiredLoadFields.forEach((field) => {
            if (this.isFieldFilled(load?.[field.name])) return

            const errorKey = `${field.name}-${id}-${index}`
            this.$set(
              this.errors,
              errorKey,
              field?.['validation-messages']?.required ?? this.$t('global.validation.required')
            )
            nextErrorKeys.push(errorKey)
          })
        })
      })

      this.loadValidationErrorKeys = nextErrorKeys

      if (nextErrorKeys.length > 0) {
        this.validationErrorKeys = [...new Set([...this.validationErrorKeys, ...nextErrorKeys])]
        this.syncErrorStates()
        return true
      }

      this.syncErrorStates()
      return false
    },
    parseData() {
      const generalData = {}
      this.basicInfoFields.forEach((field) => {
        const value = this.bulkFormValues[field.name]
        if (value == null || value == undefined) return

        switch (field.name) {
          case 'user_group_id':
            generalData[field.name] = Number(value)
            break
          default:
            generalData[field.name] = value
        }
      })

      this.computedCustomFields.forEach((field) => {
        let value = this.bulkFormValues[field.name]
        if (value == null || value == undefined) {
          return
        }

        if (field._type == 'number' && typeof value != 'number') {
          value = parseFloat(this.bulkFormValues[field.name])
        }

        generalData[field.name] = value
      })

      const deliveryOrders = []

      const ids = [...this.bulkDeliveryIds].reverse()

      ids.forEach((id) => {
        const data = { ...generalData }

        const detailFields = [...this.originFields, ...this.destinationFields]
        detailFields.forEach((field) => {
          const value = this.bulkItemValues[id]?.[field.name]

          if (value == null || value == false) return

          data[field.name] = value
        })

        let loadValues = this.bulkLoadValues[id] || []

        if (loadValues.length > 0) {
          const load = loadValues.map((load) => {
            return omitBy(load, (value) => value == null)
          })

          data.delivery_order_loads = load
        }

        deliveryOrders.push(data)
      })

      return { delivery_orders: deliveryOrders }
    },
    buildCandidateOrders(deliveryOrders = []) {
      return deliveryOrders.map((deliveryOrder) => ({
        product_ids: Array.isArray(deliveryOrder?.delivery_order_loads)
          ? deliveryOrder.delivery_order_loads
              .map((load) => load?.product_id)
              .filter((productId) => productId != null)
          : [],
      }))
    },
    showResponseError(errorResponse) {
      this.loadValidationErrorKeys = []
      const newErrors = {}
      const defaultToastMessage = this.$t('global.inputFormatError')
      let toastMessage = defaultToastMessage

      for (const [key, value] of Object.entries(errorResponse)) {
        const splittedKey = key.split('.')
        const deliveryIndex = Number(String(splittedKey[1] || '').replace(/\[|\]/g, ''))
        const id = [...this.bulkDeliveryIds].reverse()[deliveryIndex]
        const fieldKey = splittedKey[2]
        const errorCode = value[0]
        const fieldErrorMappings = this.apiErrorMappings[fieldKey] || {}
        const hasMappedFieldError = Object.prototype.hasOwnProperty.call(
          fieldErrorMappings,
          errorCode
        )
        const mappedFieldError = hasMappedFieldError ? fieldErrorMappings[errorCode] : errorCode

        if (
          this.apiErrorMappingMessages?.[fieldKey]?.[errorCode] &&
          toastMessage === defaultToastMessage
        ) {
          toastMessage = this.apiErrorMappingMessages[fieldKey][errorCode]
        }

        if (fieldKey == 'delivery_order_loads') {
          const index = Number(splittedKey[3])
          newErrors[`${splittedKey[4]}-${id}-${index}`] = value[0]

          continue
        }

        if (this.computedDeliveryFieldKeys.includes(fieldKey)) {
          newErrors[`${fieldKey}-${id}`] = mappedFieldError

          continue
        }

        newErrors[fieldKey] = mappedFieldError
      }

      this.errors = newErrors
      this.validationErrorKeys = []
      this.syncDuplicateShipmentErrors()
      this.$mcToast.error(toastMessage)
    },
    removeError(key) {
      this.$delete(this.errors, key)
      this.validationErrorKeys = this.validationErrorKeys.filter((errorKey) => errorKey !== key)
      this.loadValidationErrorKeys = this.loadValidationErrorKeys.filter(
        (errorKey) => errorKey !== key
      )
      this.syncErrorStates()
    },
    async handleSubmit(withScheduleFleetTask = false) {
      const shouldCreateFleetOrder = withScheduleFleetTask === true

      this.validationErrorKeys = []
      this.isError = {
        basicInfo: false,
        customField: false,
        deliveries: false,
      }

      this.bulkDeliveryIds.forEach((key) => {
        this.$store.dispatch('delivery/setBulkIdError', { key, value: false })
      })

      this.syncDuplicateShipmentErrors()
      if (this.duplicateShipmentErrorKeys.length > 0) {
        this.$mcToast.error(this.$t('global.inputFormatError'))
        return
      }

      let ref = this.$refs['create-bulk-form']
      const hasLoadValidationErrors = this.validateRequiredLoadFields()
      if (ref.hasErrors || hasLoadValidationErrors) {
        ref.showErrors()
        this.$mcToast.error(this.$t('global.inputFormatError'))
        this.showErrors(ref)

        return
      }

      const parseData = {
        ...this.parseData(),
        ...(shouldCreateFleetOrder ? { with_fo: true } : {}),
      }

      const res = await DeliveryOrder.bulkStore(parseData)

      if (res.errors) {
        if (res.status == 400) {
          this.showResponseError(res.errors)
        }

        return
      }

      const fleetTaskId = res?.data?.fleet_task?.id
      if (shouldCreateFleetOrder && fleetTaskId != null) {
        this.$router.push({
          name: 'Fleet Task Detail',
          params: { id: fleetTaskId },
          query: {
            totalDeliveries: res?.data?.delivery_order_count,
            isCreateFoFromBulkDo: true,
          },
        })
        return
      }
      this.$mcToast.success({
        component: 'McToastDynamicBody',
        props: {
          contents: [
            { tag: 'strong', text: res.data.delivery_order_count, class: '__status' },
            { tag: 'span', text: this.$t('delivery.title.delivery') },
            { tag: 'span', text: this.$t('delivery.message.savedAs') },
            { tag: 'strong', text: `${this.$t('delivery.status.draft')}.`, class: '__status' },
          ],
        },
      })

      this.$router.push({ name: 'Delivery' })
    },
    async handleScheduleFoFromBulkDo() {
      this.validationErrorKeys = []
      this.isError = {
        basicInfo: false,
        customField: false,
        deliveries: false,
      }

      this.bulkDeliveryIds.forEach((key) => {
        this.$store.dispatch('delivery/setBulkIdError', { key, value: false })
      })

      this.syncDuplicateShipmentErrors()
      if (this.duplicateShipmentErrorKeys.length > 0) {
        this.$mcToast.error(this.$t('global.inputFormatError'))

        return
      }

      let ref = this.$refs['create-bulk-form']
      const hasLoadValidationErrors = this.validateRequiredLoadFields()
      if (ref.hasErrors || hasLoadValidationErrors) {
        ref.showErrors()
        this.$mcToast.error(this.$t('global.inputFormatError'))
        this.showErrors(ref)

        return
      }
      const parseData = this.parseData()
      const deliveryOrderLimit = this.increaseDeliveryOrderLimit ? 100 : 50
      if (parseData.delivery_orders.length > deliveryOrderLimit) {
        this.$mcToast.error({
          component: 'McToastDynamicBody',
          props: {
            contents: [
              {
                html: deliveryOrderLimit
                  ? this.$t('delivery.alert.scheduleFailedMax100Deliveries')
                  : this.$t('delivery.alert.scheduleFailedMax50Deliveries'),
              },
            ],
          },
        })
        return
      }

      const candidateOrders = this.buildCandidateOrders(parseData.delivery_orders)
      const hasDeliveryLoads = candidateOrders.some((order) => order.product_ids.length > 0)

      if (this.hasAccessAll(['tms-cold-chain:read']) && hasDeliveryLoads) {
        const dataTemp = await DeliveryOrder.BulkValidateDo({
          validation_type: 'HAS_TEMPERATURE_GAP',
          candidate_delivery_orders: candidateOrders,
        })

        if (dataTemp.errors) {
          this.$mcToast.error(this.$t('delivery.alert.requestError'))
          return
        }

        const getTemp = dataTemp?.data

        this.dataGapTemprerature = getTemp

        const savedPref = localStorage.getItem('hideTemperatureSummary')

        if (savedPref) {
          this.dontShowAgain = JSON.parse(savedPref)
        }

        if (
          !this.skipCheckTemp &&
          !this.dontShowAgain &&
          getTemp.min !== null &&
          getTemp.max !== null
        ) {
          this.dataConfirmTemp = getTemp
          this.$bvModal.show('modal-confirm-summary-temperature-attach')
          return
        }
      }

      this.$bvModal.show('modal-schedule-fleet-task')
    },
    handleApplyCreateFO() {
      this.skipCheckTemp = true
      this.$bvModal.show('modal-schedule-fleet-task')
    },
    handleSubmitCreateFO() {
      this.$bvModal.hide('modal-schedule-fleet-task')
      this.handleSubmit(true)
    },
  },
}
</script>

<style lang="scss" scoped>
.mc-list-header {
  margin-bottom: 24px;
}

.mc-create-bulk__header-actions {
  flex-wrap: wrap;
  justify-content: flex-end;
}

@media screen and (max-width: 767px) {
  .mc-list-header {
    margin-bottom: 0px;
  }

  :deep(.page-content) {
    padding-bottom: 70px !important;
  }

  :deep(.banner-header) {
    padding: 12px 18px 16px;
    background: $white;
  }

  :deep(.banner-header .row) {
    margin: 0;
  }

  :deep(.banner-header .row > .col-12) {
    padding-left: 0;
    padding-right: 0;
  }

  :deep(.banner-header .row > .col-12.d-flex) {
    align-items: flex-start !important;
  }

  :deep(.header-container) {
    top: var(--mc-create-bulk-sticky-top, 70px);
  }

  .mc-list-header--sticky {
    box-sizing: border-box;

    :deep(.banner-header) {
      margin: 0;
      border-bottom: 1px solid #edf2f7;
      box-shadow: 0 10px 20px rgba(15, 23, 42, 0.08);
    }
  }

  .mc-create-bulk__header-actions--mobile {
    margin-top: 12px;
    width: 100%;
    justify-content: flex-start;
  }

  :deep(.mc-create-bulk__cancel) {
    margin-right: 12px;
    flex: 0 0 auto;
  }

  :deep(.mc-create-bulk__primary-action) {
    flex: 1 1 auto;
    min-width: 0;
  }

  :deep(.mc-create-bulk__primary-action .mc-button) {
    width: 100%;
    justify-content: center;
  }

  .mc-create-bulk__mobile-bottom-spacer {
    width: 100%;
    flex: 0 0 auto;
  }
}
</style>
